import React, { FC, useCallback, useState, useEffect, useRef } from 'react'
import { Grid, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import AddIcon from '@mui/icons-material/Add'
import { FormProvider, useForm } from 'react-hook-form'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { classValidatorResolver } from '@hookform/resolvers/class-validator'
import { OnboardingDirectorDto } from '@sparling/financial-common'
import { setStepperFormData } from '../../../../services/setStepperFormData'
import { getStepperFormData } from '../../../../services/getStepperFormData'
import { TitleOnBoarding } from '../../../../components/TitleOnBoarding'
import { GreenButton } from '../../../../components/Buttons/GreenButton'
import { CInputField } from '../../../../components/CustomInputs/CInputField'
import { InputCheckbox } from '../../../../components/CustomInputs/InputCheckbox'
import { AddAnotherTable } from '../../../../components/AddAnotherTable'
import useDebounce from '../../../../hooks/useDebounce'
import { useRest } from '../../../../components/Context'
import { errorMessage } from '../../../../constants/errorMessages'

const resolver = classValidatorResolver(OnboardingDirectorDto)

const useStyles = makeStyles({
  container: {
    margin: '0 auto',
    maxWidth: 1184,
    fontFamily: 'Montserrat, sans-serif',
    fontWeight: 'bold',
  },
  bold: {
    fontWeight: 'bold',
  },
  addInput: {
    paddingLeft: 15,
    color: '#584DE2',
    cursor: 'pointer',
    '&:hover': {
      color: '#9b95ee',
    },
  },
  addInputDisabled: {
    paddingLeft: 15,
    color: '#9b95ee',
    cursor: 'disabled',
    pointerEvents: 'none',
  },
  addInputLabel: {
    fontWeight: 'bold',
  },
  formContainer: {
    position: 'relative',
    width: '632px',
    marginTop: '81px',
  },
  buttonWrap: {
    width: 'auto',
    marginTop: '72px',
    alignItems: 'center',
  },
  skipLabel: {
    position: 'absolute',
    right: 0,
    fontWeight: 'bold',
    color: '#584DE2',
    cursor: 'pointer',
  },
})

interface AddDirectorItem {
  firstName: string
  lastName: string
  email: string
  executingTrades: boolean
}

interface IAdddirector {
  title: string
}

export const AddDirector: FC<IAdddirector> = ({ title }) => {
  const classes = useStyles()
  const history = useHistory()
  const location = useLocation()
  const [activeStep] = useState(4)
  const [isEdit, setIsEdit] = useState<boolean>(false)
  const [editIndex, setEditIndex] = useState<number | undefined>()
  const [formDisabled, seFormDisabled] = useState<boolean>(true)
  const [formSubmitDisabled, seFormSubmitDisabled] = useState<boolean>(true)
  const [isDisabled, setDisabled] = useState<boolean>(false)
  const [isFilled, seIsFilled] = useState<boolean>(false)
  const [Email, setEmail] = useState<string | undefined>()
  // eslint-disable-next-line
  const [formStorage, seFormStorage] = useState<any>([])
  const formRef = useRef<HTMLFormElement>(null)
  const debouncedValue = useDebounce<string | undefined>(Email, 200)
  const { uniqKey } = useParams() as { [key: string]: string }
  const { rxios } = useRest()

  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      role: 'director',
      executingTrades: false,
    },
    resolver,
  })

  const { handleSubmit, setValue, formState, watch } = formMethods

  const emailVerification = useCallback(
    (email: string | undefined) => {
      rxios
        .post('company/onboarding/email-verification', {
          email,
          hash: uniqKey,
        })
        .subscribe(
          // eslint-disable-next-line
          (next: any) => {
            setDisabled(false)
          },
          (error) => {
            toast.error(error.response.data.message)
            setDisabled(true)
          },
        )
      // eslint-disable-next-line
    },
    [rxios, uniqKey],
  )

  useEffect(() => {
    if (/^[a-zA-Z0-9._-]+@(?:[a-zA-Z0-9-]+\.)+([a-zA-Z0-9-]{2,})+$/.test(Email as string)) {
      // eslint-disable-next-line
      if (Email === formStorage?.company?.email || formStorage?.ubo?.find((item: any) => item.email === Email)) {
        toast.error(errorMessage.EMAIL_IS_ALREADY_REGISTERED)
        setDisabled(true)
      } else if (Email !== null) {
        emailVerification(Email)
      }
    }
    // eslint-disable-next-line
  }, [debouncedValue])

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'email') setEmail(value[name])
    })
    return () => subscription.unsubscribe()
  }, [watch])

  const updateFormStorage = useCallback(() => {
    const storage = getStepperFormData()
    seFormStorage(storage)
  }, [])

  const addAnotherCompany = () => {
    const getNewValue = formMethods.getValues()
    if (getNewValue.firstName && getNewValue.lastName && getNewValue.email) {
      if (!isEdit) {
        setStepperFormData(
          // eslint-disable-next-line no-prototype-builtins
          !formStorage.hasOwnProperty('director')
            ? { ...formStorage, director: [getNewValue] }
            : { ...formStorage, director: [...formStorage.director, getNewValue] },
        )
      } else {
        const storage = getStepperFormData()
        const newFormStorage = Object.assign(storage)
        const { director } = newFormStorage
        // eslint-disable-next-line
        const newDirector = director.filter((item: any, i: number) => i !== editIndex)
        newDirector.push(getNewValue)
        setStepperFormData({ ...formStorage, director: [...newDirector] })
      }
      updateFormStorage()
      formMethods.reset({
        firstName: '',
        lastName: '',
        email: '',
        role: 'director',
        executingTrades: false,
      })
      setIsEdit(false)
      setEditIndex(undefined)
      seFormDisabled(true)
      seIsFilled(false)
    }
  }

  const formValid = useCallback(() => {
    const getNewValue = formMethods.getValues()
    const { firstName, lastName, email } = getNewValue
    seIsFilled(false)
    if (firstName.length && lastName.length && email.length) seIsFilled(true)
  }, [formMethods])

  useEffect(() => {
    formRef?.current?.addEventListener('change', formValid)

    if (isFilled) {
      const { firstName, lastName, email } = formState.errors
      if (!firstName && !lastName && !email) {
        seFormDisabled(false)
      }
    } else {
      seFormDisabled(true)
    }

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      formRef?.current?.removeEventListener('change', formValid)
    }
  }, [formValid, isFilled, formState])

  useEffect(() => {
    updateFormStorage()
  }, [updateFormStorage])

  useEffect(() => {
    if (formStorage?.director?.length) {
      seFormSubmitDisabled(false)
    } else {
      seFormSubmitDisabled(formDisabled)
    }
  }, [formStorage, formMethods, formDisabled])

  const onSubmit = useCallback(
    (newValue) => {
      setStepperFormData(
        // eslint-disable-next-line no-prototype-builtins
        !formStorage.hasOwnProperty('director')
          ? { ...formStorage, director: [newValue] }
          : { ...formStorage, director: [...formStorage.director, newValue] },
      )
      history.push({
        ...location,
        state: {
          activeStep,
        },
      })
    },
    [activeStep, history, location, formStorage],
  )

  const onEdit = useCallback(
    (index: number) => {
      const getNewValue = formMethods.getValues()
      let newFormStorage = []
      if (getNewValue.firstName && getNewValue.lastName && getNewValue.email) {
        const storage = getStepperFormData()
        newFormStorage = Object.assign(storage)
      } else {
        newFormStorage = Object.assign(formStorage)
      }
      const { director } = newFormStorage
      const { firstName, lastName, email, executingTrades } = director[index]
      setValue('firstName', firstName, { shouldValidate: true })
      setValue('lastName', lastName, { shouldValidate: true })
      setValue('email', email, { shouldValidate: true })
      setValue('executingTrades', executingTrades, { shouldValidate: false })
      setIsEdit(true)
      setEditIndex(index)
      seFormDisabled(false)
      seIsFilled(true)
    },
    [formStorage, setValue, formMethods],
  )

  const onDelete = useCallback(
    (index: number) => {
      const newFormStorage = Object.assign(formStorage)
      const { director } = newFormStorage
      // eslint-disable-next-line
      const newDirector = director.filter((item: any, i: number) => i !== index)
      setStepperFormData({ ...formStorage, director: newDirector })
      updateFormStorage()
      setIsEdit(false)
      setEditIndex(undefined)
    },
    [formStorage, updateFormStorage],
  )

  const onSubmitForm = useCallback(
    // eslint-disable-next-line
    (e: any) => {
      e.preventDefault()
      if (formStorage?.director?.length) {
        const getNewValue = formMethods.getValues()
        if (!formDisabled) {
          handleSubmit(onSubmit)()
        } else if (
          (formDisabled && getNewValue.firstName) ||
          (formDisabled && getNewValue.lastName) ||
          (formDisabled && getNewValue.email)
        ) {
          handleSubmit(onSubmit)()
        } else {
          history.push({
            ...location,
            state: {
              activeStep,
            },
          })
        }
      } else {
        handleSubmit(onSubmit)()
      }
    },
    [activeStep, formMethods, formStorage, handleSubmit, history, location, onSubmit, formDisabled],
  )

  const onSkipStep = useCallback(() => {
    history.push({
      ...location,
      state: {
        activeStep,
      },
    })
  }, [activeStep, history, location])

  return (
    <FormProvider {...formMethods}>
      <form ref={formRef} onSubmit={onSubmitForm}>
        <Grid className={classes.container} container direction="column" alignItems="center">
          <TitleOnBoarding title={title} leftArrow activeStep={activeStep} />
          <Grid item container className={classes.formContainer} direction="column" alignItems={'center'}>
            <Grid item container justifyContent={'space-between'}>
              <CInputField name={'firstName'} label={'First Name'} />
              <CInputField name={'lastName'} label={'Last Name'} />
              <CInputField name={'role'} label={'role'} type="hidden" />
            </Grid>
            <Grid item container>
              <CInputField name={'email'} label={'Director Email'} isCompanyEmail />
            </Grid>
            <Grid item container alignItems={'center'} justifyContent={'space-between'} wrap={'nowrap'}>
              <Grid item>
                <InputCheckbox
                  name={'executingTrades'}
                  label={'Director will be executing trades'}
                  // checked={formMethods.getValues('executingTrades')}
                  // handleChange={handleChange}
                />
              </Grid>
              <Grid
                item
                container
                xs={4}
                className={formDisabled || isDisabled ? classes.addInputDisabled : classes.addInput}
                alignItems={'center'}
                onClick={addAnotherCompany}
              >
                <AddIcon />
                <Typography className={classes.addInputLabel} component={'span'} variant={'body2'}>
                  Add another director
                </Typography>
              </Grid>
            </Grid>
            <Grid>
              <hr style={{ color: '#ebebeb', opacity: 0.6 }} />
              {/* eslint-disable-next-line no-prototype-builtins */}
              {formStorage.hasOwnProperty('director') &&
                formStorage.director.map((item: AddDirectorItem, i: number) => {
                  return (
                    <AddAnotherTable
                      key={i}
                      fullName={`${item.firstName} ${item.lastName}`}
                      email={item.email}
                      onEdit={() => onEdit(i)}
                      onDelete={() => onDelete(i)}
                      hidden={editIndex === i}
                    />
                  )
                })}
            </Grid>

            <Grid item container xs={3} className={classes.buttonWrap}>
              <GreenButton disabled={formSubmitDisabled || isDisabled} text="NEXT" type="submit" width={321} />
              <Typography className={classes.skipLabel} onClick={onSkipStep} component={'span'} variant={'body2'}>
                Skip
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  )
}
